// src/components/BottomTabs.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Users,
  Building2,
  LogOut,
  UserCog,
  Stethoscope,
  Settings,
  ListChecks,
  LucideGraduationCap,
  HeartPulse,
  QrCode,
  Hospital,
  Printer,
  Link,
  ClipboardList,
  Settings2,
  BarChart,
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { socket } from '../socket';
import { listTodos } from '../api/todos';
import { byEtab } from '../api/patients';
import { createPortal } from 'react-dom';
import { listExternalLinks } from '../api/externalLinks';
import { theme } from '../config/theme';
import {
  getQuestionToAnswer as fetchQuestionToAnswer,
  answerQuestion,
} from '../api/formationContinue';

export default function BottomTabs() {
  const { pathname } = useLocation();
  const {
    user,
    profile,
    etablissement,
    nbQCMAnswered: numberQCMAnswered,
    handleSetNbQCMAnswered: setNumberQCMAnswered,
    nbCorrectQCMAnswered: numberCorrectQCMAnswered,
    handleSetNbCorrectQCMAnswered: setNumberCorrectQCMAnswered,
  } = useUser();
  const role = user?.role || localStorage.getItem('role') || sessionStorage.getItem('role');
  const isAdmin = role === 'admin';
  const isCare = ['infirmiere', 'medecin', 'admin'].includes(role);
  const [open, setOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [passagesSallesCount, setPassagesSallesCount] = useState(0);
  const [passagesArriveeCount, setPassagesArriveeCount] = useState(0);
  const [links, setLinks] = useState([]);
  const [linksChoiceOpen, setLinksChoiceOpen] = useState(false);
  const [QCMOpen, setQCMOpen] = useState(false);
  const [questionToAnswer, setQuestionToAnswer] = useState(null);
  const [chosenAnswer, setChosenAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answers, setAnswers] = useState([]);
  const maxQuestionsToAnswer = 10;

  // Rafraîchit les liens immédiatement à l'ouverture de la modale
  const openLinksChoice = () => {
    refreshLinks();
    setLinksChoiceOpen(true);
  };

  const openQCM = async () => {
    setQCMOpen(true);
  };

  const onAnswerQCM = async (answer) => {
    try {
      setChosenAnswer(answer);
      await answerQuestion({
        profileId: profile.profile_id,
        questionId: questionToAnswer.id,
        answer,
        role: role?.length >= 3 ? role.slice(0, 3) : 'none',
      });
      setAnswerSubmitted(true);
      if (answer === 'ao') {
        setNumberCorrectQCMAnswered(numberCorrectQCMAnswered + 1);
      }
    } catch (err) {
      console.error('Erreur de traitement de la réponse:', err);
    }
  };

  const nextQuestion = async () => {
    setChosenAnswer(null);
    setAnswerSubmitted(false);
    getQuestionToAnswer();
    setNumberQCMAnswered(numberQCMAnswered + 1);
  };

  const base = [
    { to: '/arrivee', label: 'Arrivée', Icon: Users, badge: passagesArriveeCount || undefined },
  ];

  base.push({
    to: '/passages',
    label: 'Passages',
    Icon: HeartPulse,
    badge: passagesSallesCount || undefined,
  });

  base.push({
    to: '/salles',
    label: 'Salles',
    Icon: Building2,
  });

  base.push({ to: '/todos', label: 'To Do', Icon: ListChecks, badge: todos.length || undefined });
  if (questionToAnswer) {
    base.push({
      action: openQCM,
      label: 'Formation',
      Icon: LucideGraduationCap,
      badge: maxQuestionsToAnswer - numberQCMAnswered || undefined,
    });
  }
  base.push({ to: '/sortie', label: 'Sortie', Icon: LogOut });
  base.push({ action: openLinksChoice, label: 'Liens', Icon: Link });

  function refetchTodos() {
    if (profile) {
      listTodos({ statut: 'nouveau', profileId: profile.profile_id })
        .then((rows) => {
          setTodos(Array.isArray(rows) ? rows : []);
        })
        .catch((e) => {
          console.error('[todos] refreshTodos failed:', e);
        });
    }
  }

  function getQuestionToAnswer() {
    if (profile) {
      fetchQuestionToAnswer(profile.profile_id, role?.length >= 3 ? role.slice(0, 3) : 'none')
        .then((rows) => {
          setQuestionToAnswer(Array.isArray(rows) && rows.length > 0 ? rows[0] : null);
          setAnswers(
            Array.isArray(rows) && rows.length > 0
              ? [
                  { text: rows[0].a_ok, answer: 'ao' },
                  { text: rows[0].a_w1, answer: 'w1' },
                  { text: rows[0].a_w2, answer: 'w2' },
                ].sort(() => Math.random() - 0.5)
              : [],
          );
        })
        .catch((e) => {
          console.error('[QCM] getQuestionToAnswer failed:', e);
        });
    }
  }

  function refetchPassages() {
    if (profile && etablissement) {
      byEtab(etablissement.etablissement_id)
        .then((rows) => {
          const passagesArrivee = rows.filter((obj) => obj.salle_id === null);
          setPassagesArriveeCount(passagesArrivee.length);
          const passagesSalles = rows.filter((obj) => obj.salle_id != null);
          setPassagesSallesCount(passagesSalles.length);
        })
        .catch((e) => {
          console.error('refetchPassages failed:', e);
        });
    }
  }

  // Fonction de rechargement avec tri systématique par "ordre"
  const refreshLinks = async () => {
    try {
      const fel = await listExternalLinks();
      const sorted = Array.isArray(fel)
        ? [...fel].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
        : [];
      setLinks(sorted);
    } catch (e) {
      console.log('Error fetching links');
    }
  };

  function getPassagesFromList(list) {
    if (profile && etablissement) {
      const validSalleIds = new Set(etablissement.salles.map((s) => s.salle_id));
      const passagesSallesCnt = list.filter(
        (p) =>
          validSalleIds.has(p.salle_id) &&
          p.currentStep >= 1 &&
          p.currentStep <= 6 &&
          p.etablissement_id === etablissement.etablissement_id,
      ).length;
      setPassagesSallesCount(passagesSallesCnt);

      const passagesArriveeCnt = list.filter(
        (p) =>
          p.salle_id === null &&
          p.etablissement_id === etablissement.etablissement_id &&
          p.currentStep <= 6,
      ).length;
      setPassagesArriveeCount(passagesArriveeCnt);
    }
  }

  useEffect(() => {
    refetchTodos();
    refetchPassages();
    refreshLinks();
    getQuestionToAnswer();

    const handleExternalLinksUpdated = (updated) => {
      if (Array.isArray(updated) && updated.length > 0) {
        const sorted = [...updated].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
        setLinks(sorted);
      } else {
        refreshLinks();
      }
    };

    socket.on('todoCreated', refetchTodos);
    socket.on('todoUpdated', refetchTodos);
    socket.on('patientsList', (list) => {
      getPassagesFromList(list);
    });
    socket.on('patients_list', refetchPassages);
    socket.on('patientUpdated', refetchPassages);
    socket.on('patientCreated', refetchPassages);
    socket.on('patient_created', refetchPassages);
    socket.on('patientDeleted', refetchPassages);
    socket.on('patientsCleared', refetchPassages);
    socket.on('externalLinksUpdated', handleExternalLinksUpdated);

    return () => {
      socket.off('todoCreated', refetchTodos);
      socket.off('todoUpdated', refetchTodos);
      socket.off('patientsList', (list) => {
        getPassagesFromList(list);
      });
      socket.off('patients_list', refetchPassages);
      socket.off('patientUpdated', refetchPassages);
      socket.off('patientCreated', refetchPassages);
      socket.off('patient_created', refetchPassages);
      socket.off('patientDeleted', refetchPassages);
      socket.off('patientsCleared', refetchPassages);
      socket.off('externalLinksUpdated', handleExternalLinksUpdated);
    };
  }, []);

  useEffect(() => {
    refetchPassages('profile, etablissement');
  }, [profile, etablissement]);

  const adminItems = [
    { to: '/admin/audit', label: "Journal d'activité", Icon: ClipboardList },
    { to: '/admin/users', label: 'Comptes', Icon: UserCog },
    { to: '/admin/stats', label: 'Statistiques', Icon: BarChart },
    { to: '/admin/traumato', label: 'Traumatologie', Icon: Stethoscope },
    { to: '/admin/print-codes', label: 'Codes écran', Icon: QrCode },
    { to: '/admin/salles', label: 'Salles et groupes', Icon: Hospital },
    { to: '/admin/equipements', label: 'Équipements', Icon: Printer },
    { to: '/admin/parametres', label: 'Paramètres', Icon: Settings },
    { to: '/admin/todos', label: 'Configuration des To Do', Icon: Settings2 },
    { to: '/admin/formation-continue', label: 'Formation continue', Icon: LucideGraduationCap },
  ];

  if (isAdmin) {
    base.push({ to: '/admin', label: 'Administration', Icon: UserCog, admin: true });
  }

  const gridColsMap = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
    9: 'grid-cols-9',
  };
  const grid = gridColsMap[base.length] || 'grid-cols-6';

  return (
    <>
      {linksChoiceOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/60">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setLinksChoiceOpen(false)}
            />
            <div className="relative z-10 mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 gap-2 w-[400px] max-w-[80vh]">
              <p>Redirection vers ...</p>
              {links.map((link) => {
                return (
                  <a
                    key={'chng_lnk_to_' + link.id}
                    className="py-2 border-2 rounded border-red-800 bg-white text-red-600 text-center block font-medium"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.libelle}
                  </a>
                );
              })}
            </div>
          </div>,
          document.body,
        )}

      {QCMOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/60">
            <div className="absolute inset-0 bg-black/30" onClick={() => setQCMOpen(false)} />
            <div className="relative z-10 mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 gap-2 w-[400px] max-w-[80vh]">
              {numberQCMAnswered === maxQuestionsToAnswer ? (
                <p>
                  Vous avez obtenu <strong>{numberCorrectQCMAnswered}</strong> bonnes réponses sur{' '}
                  <strong>{maxQuestionsToAnswer}</strong> !
                </p>
              ) : questionToAnswer ? (
                <>
                  <p>
                    Question {numberQCMAnswered + 1} sur {maxQuestionsToAnswer}
                  </p>
                  {
                    <>
                      <h2 className="mb-6 text-xl font-semibold text-gray-900">
                        {questionToAnswer.question}
                      </h2>

                      <div className="space-y-3">
                        {answers.map((answer, index) => (
                          <button
                            key={index}
                            onClick={() => onAnswerQCM(answer.answer)}
                            disabled={answerSubmitted}
                            className={`
                                w-full rounded-lg border px-4 py-3 text-left transition
                                ${
                                  answerSubmitted && answer.answer === 'ao'
                                    ? 'border-green-500 bg-green-100 text-green-800'
                                    : answerSubmitted &&
                                        answer.answer === chosenAnswer &&
                                        answer.answer !== 'ao'
                                      ? 'border-red-500 bg-red-100 text-red-800'
                                      : 'border-gray-300 bg-white text-gray-700 hover:bg-blue-50'
                                }
                              `}
                          >
                            {answer.text}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={nextQuestion}
                        disabled={!answerSubmitted}
                        className={`
                          mt-4 w-full rounded-lg px-4 py-3 font-medium transition
                          ${
                            answerSubmitted
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'cursor-not-allowed bg-gray-200 text-gray-400'
                          }
                        `}
                      >
                        Question suivante
                      </button>
                    </>
                  }
                </>
              ) : (
                <p className="text-center text-gray-500">
                  Pas de question disponible pour le moment ...
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* cache la barre quand le body a .no-tabs (overlay cotation) */}
      <style>{`.no-tabs .app-bottombar{display:none}`}</style>

      <nav className="app-bottombar pointer-events-auto fixed bottom-3 left-1/2 z-40 w-[min(100%,1200px)] -translate-x-1/2 px-3">
        <div
          className={`grid ${grid} rounded-2xl bg-gradient-to-r ${theme.bottombarBg} p-2 shadow-xl`}
          style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
        >
          {base.map((it) => {
            const active = it.to ? pathname.startsWith(it.to) : false;

            const Inner = (
              <div
                className={`relative group flex items-center justify-center gap-0 sm:gap-2 rounded-xl px-3 py-2 transition-all
                  ${active ? `bg-white ${theme.activeText}` : 'text-white/90 hover:text-white'}`}
              >
                <it.Icon className={`h-6 w-6 ${active ? theme.activeText : 'text-white'}`} />
                <span
                  className={`hidden sm:inline text-[11px] ${active ? 'font-semibold' : 'opacity-90'}`}
                >
                  {it.label}
                </span>
                {it.badge && (
                  <div
                    className={
                      'absolute -top-5 -right-[2px] md:right-[42px] flex items-center justify-center ' +
                      'w-7 h-7 rounded-full text-[12px] font-bold ring-2 ' +
                      (active ? theme.badgeActive : theme.badgeInactive)
                    }
                  >
                    {it.badge}
                  </div>
                )}
                <span className="sr-only">{it.label}</span>
              </div>
            );

            if (it.admin) {
              return (
                <div key="admin" className="relative">
                  <button
                    aria-label="Administration"
                    onClick={() => setOpen((o) => !o)}
                    className="w-full focus:outline-none"
                  >
                    {Inner}
                  </button>

                  {open && (
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 w-48 rounded-xl bg-white shadow-lg border border-slate-200">
                      <div className="divide-y">
                        {adminItems.map(({ to, label, Icon }) => (
                          <NavLink
                            key={to}
                            to={to}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 px-4 py-3 hover:bg-slate-50"
                          >
                            <Icon size={16} />
                            <span>{label}</span>
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return it.to ? (
              <NavLink key={it.to} to={it.to} className="focus:outline-none" aria-label={it.label}>
                {Inner}
              </NavLink>
            ) : (
              <button
                key={it.label}
                onClick={it.action}
                className="focus:outline-none"
                aria-label={it.label}
              >
                {Inner}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}